import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@zenbusiness/application-commons-graphql';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { CARD_STATUS, CARD_TYPE, CREATE_CARD_ERROR } from '#client/constants/cards';
import { KEY_PARAM_MODAL, MODAL_NAMES } from '#client/constants/modals';
import { GENERATING_SENSITIVE_DATA } from '#client/constants/time';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConst from '#client/hooks/useConst';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { CREATE_CARD_MUTATION } from '#client/mutations';
import { GET_CARDS_QUERY, GET_CARDS_PIN_STATUS_QUERY } from '#client/queries';

export const CardsContext = createContext();
CardsContext.displayName = 'CardsContext';

const useCardsContext = () => {
  const context = useContext(CardsContext);

  if (typeof context === 'undefined') {
    throw new Error('useCardsContext must be used within a CardsContext.Provider');
  }

  return context;
};

export function CardsProvider({ children }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId, isEnrolled, isEnabled, isValidMfa, isFetchingMfaStatus } =
    useUnitAccountId();
  const { appStatus, loading: appStatusLoading } = useApplicationStatusContext();
  const { customerToken } = useCustomerToken({ type: TOKEN_TYPES.CARDS_SENSITIVE, prefetch: true });
  const { checkIsOpeningModalExcept } = useCheckOpenModal();
  const { createCommonErrorToast, createErrorToast, createSuccessToast } = useToastContext();
  const [searchParams] = useSearchParams();
  const modalName = searchParams.get(KEY_PARAM_MODAL);

  const [createCard, { loading: isCreateCardLoading, errors: createCardErrors, error: cardError }] =
    useMutation(CREATE_CARD_MUTATION, {
      noThrow: true
    });

  useLogError(createCardErrors, cardError);
  const idempotencyKey = useConst(uuidv4);

  const isSkipQuery =
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    // README: When the the account has been closed on Unit dashboard, then reload page.
    // we call the API to get the status and update the banking account (Unit account) in local storage based on the app status.
    // Meanwhile, other APIs that depend on the banking account are not triggered correctly
    appStatusLoading ||
    (appStatus && appStatus === DB_APPLICATION_STATUS.CLOSED) ||
    !unitAccountId ||
    !businessEntityUuid ||
    checkIsOpeningModalExcept([MODAL_NAMES.SET_PIN_MODAL, MODAL_NAMES.ACTIVATE_CARD]);

  const {
    error: cardsError,
    errors: cardsErrors,
    loading: cardsLoading,
    data,
    refetch: refetchCard
  } = useQuery(GET_CARDS_QUERY, {
    variables: {
      listCardParams: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        filter: {
          status: [
            CARD_STATUS.ACTIVE,
            CARD_STATUS.INACTIVE,
            CARD_STATUS.FROZEN,
            CARD_STATUS.SUSPECTED_FRAUD
          ]
        }
      }
    },
    noThrow: true,
    useCache: false,
    skip: isSkipQuery && modalName !== MODAL_NAMES.DEBIT_CARD_MAILING
  });

  useLogError(cardsErrors, cardsError);
  const physicalCard = useMemo(() => {
    /**
     * Filter the cards that:
     * 1. Enable feature and the physical card has status is active, frozen, inactive or suspected fraud
     * 2. Disable feature and the physical card has status is active, frozen or inactive
     */
    const filteredCards = (data?.cards?.cards ?? []).filter((card) => {
      const isBusinessDebitCard = card.type === CARD_TYPE.BUSINESS_DEBIT_CARD;

      return isBusinessDebitCard;
    });

    // Take the first card in the filtering list
    return filteredCards[0];
  }, [data]);

  const virtualCard = useMemo(
    () =>
      (data?.cards?.cards ?? []).find(
        (card) =>
          card.type === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD &&
          card.status !== CARD_STATUS.SUSPECTED_FRAUD
      ),
    [data]
  );

  const notHasPhysicalCard = !!data && !physicalCard;

  const notHasVirtualCard = !!data && !virtualCard;

  const [isGeneratingSensitiveData, setIsGeneratingSensitiveData] = useState(false);

  const createCardParams = useMemo(
    () => ({
      input: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        attributes: {
          type: CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD,
          idempotencyKey
        }
      }
    }),
    [businessEntityUuid, unitAccountId, idempotencyKey]
  );

  const handleCreateCard = useCallback(async () => {
    if (isCreateCardLoading) {
      return;
    }
    const { error: createCardError } = await createCard({
      variables: createCardParams,
      skip: !unitAccountId
    });

    if (createCardError) {
      createErrorToast({
        title: CREATE_CARD_ERROR.title,
        description: CREATE_CARD_ERROR.description,
        type: 'error',
        error: createCardError
      });
    } else {
      setIsGeneratingSensitiveData(true);
      setTimeout(() => setIsGeneratingSensitiveData(false), GENERATING_SENSITIVE_DATA);
      refetchCard();
      createSuccessToast({
        title: 'Your new virtual card is ready to use',
        description: 'It’s in your dashboard under Banking.'
      });
    }
  }, [
    isCreateCardLoading,
    createCard,
    createCardParams,
    unitAccountId,
    createErrorToast,
    refetchCard,
    createSuccessToast
  ]);

  const {
    error: pinError,
    errors: pinErrors,
    loading: pinStatusLoading,
    data: cardPinData,
    refetch: refetchCardPinStatus
  } = useQuery(GET_CARDS_PIN_STATUS_QUERY, {
    variables: {
      params: {
        id: physicalCard?.id,
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        customerToken
      }
    },
    noThrow: true,
    skip:
      isSkipQuery || !physicalCard || !customerToken || physicalCard?.status !== CARD_STATUS.ACTIVE
  });

  useLogError(pinErrors, pinError);
  // handle show Toast error
  useEffect(() => {
    const errors = cardsError || pinError;
    if (errors) {
      createCommonErrorToast({ error: errors });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsError, pinError]);

  const value = useMemo(
    () => ({
      physicalCard,
      virtualCard,
      notHasPhysicalCard,
      notHasVirtualCard,
      physicalCardPinStatus: cardPinData?.cardPinStatus ?? null,
      loading: cardsLoading || pinStatusLoading,
      error: cardsError || pinError,
      cardsError,
      cardsLoading,
      refetchCard,
      refetchCardPinStatus,
      isGeneratingSensitiveData,
      setIsGeneratingSensitiveData,
      handleCreateCard,
      isCreateCardLoading
    }),
    [
      cardPinData?.cardPinStatus,
      cardsError,
      cardsLoading,
      notHasPhysicalCard,
      notHasVirtualCard,
      physicalCard,
      pinError,
      pinStatusLoading,
      refetchCard,
      refetchCardPinStatus,
      virtualCard,
      isGeneratingSensitiveData,
      handleCreateCard,
      isCreateCardLoading
    ]
  );

  return <CardsContext.Provider value={value}>{children}</CardsContext.Provider>;
}

export default useCardsContext;
